import React from 'react';
import { Check, Lock } from 'lucide-react';
import { ConnectorCatalogEntry } from '../../types';
import { connectorPresentation } from './connectorPresentation';

interface ConnectorOptionProps {
  connector: ConnectorCatalogEntry;
  onSelect: () => void;
}

/**
 * One installable vendor in the catalog.
 *
 * A connector is unselectable for two different reasons and the card says which: already installed,
 * or blocked because another installed connector owns one of its provider ports. A port has exactly
 * one owner per tenant, so telling the merchant here — naming the incumbent — is what keeps them
 * from filling in a setup form that would be refused on submit.
 */
const ConnectorOption: React.FC<ConnectorOptionProps> = ({ connector, onSelect }) => {
  const presentation = connectorPresentation(connector.id);
  const Icon = presentation.icon;
  const disabled = connector.installed || !connector.available;

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onSelect}
      disabled={disabled}
      className={`text-left p-4 rounded-lg border transition-colors w-full ${
        disabled
          ? 'border-gray-200 dark:border-border-color opacity-60 cursor-not-allowed'
          : 'border-gray-200 dark:border-border-color hover:border-primary hover:bg-primary/5'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${presentation.bgColor}`}>
          <Icon className={`w-5 h-5 ${presentation.color}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium dark:text-white text-gray-900">{connector.label}</span>
            {connector.installed && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 flex items-center gap-1">
                <Check className="w-3 h-3" />
                Installed
              </span>
            )}
            {!connector.installed && !connector.available && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Unavailable
              </span>
            )}
          </div>
          {connector.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{connector.description}</p>
          )}
          {!connector.installed && connector.blockedBy && (
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
              {connector.blockedBy.connectorId} already provides{' '}
              {connector.blockedBy.portId.split('.').slice(-2, -1)}. Remove it first to switch.
            </p>
          )}
        </div>
      </div>
    </button>
  );
};

export default ConnectorOption;
